import { useCallback, useEffect, useMemo, useState } from 'react';
import { msg, Trans } from '@lingui/macro';
import { useLingui } from '@lingui/react';
import {
  Box,
  Button,
  Paper,
  Stack,
  Tooltip,
  Typography,
  useTheme,
} from '@mui/material';
import {
  GridRowModes,
  GridActionsCellItem,
  GridRowEditStopReasons,
  useGridApiRef,
} from '@mui/x-data-grid';
import ChecklistIcon from '@mui/icons-material/Checklist';
import { StripedDataGrid } from '../tables/StripedDataGrid';
import RoadFclassPickerDialog from '../admin/road-fclass-picker-dialog';
import SvgCustomIcon from '../icons/SvgCustomIcon';

const paginationModel = { page: 0, pageSize: 50 };

const FCLASS_KEYS = new Set(['included_fclasses', 'excluded_fclasses']);

function parseJsonArray(raw) {
  if (raw == null || raw === '') return null;
  try {
    const parsed = typeof raw === 'string' ? JSON.parse(raw) : raw;
    return Array.isArray(parsed) ? parsed : null;
  } catch {
    return null;
  }
}

function formatFclassSummary(raw, _) {
  const arr = parseJsonArray(raw);
  if (arr == null || arr.length === 0) return _(msg`None (null)`);
  if (arr.length <= 3) return arr.join(', ');
  return _(msg`${arr.length} selected`);
}

function isParameterVisible(p) {
  if (p == null) return false
  const flag = p.isVisible ?? p.IsVisible
  return flag === true || flag === 1 || flag === 'true'
}

function filterVisibleParameters(list) {
  if (!Array.isArray(list) || list.length === 0) return []
  const anyHasFlag = list.some((p) => p?.isVisible !== undefined || p?.IsVisible !== undefined)
  // Pre-migration payloads have no flag — keep previous behaviour.
  if (!anyHasFlag) return list
  return list.filter(isParameterVisible)
}

export default function RoadRiskMapParametersTable({ parameters, modifyParameter }) {
  const { _ } = useLingui();
  const theme = useTheme();
  const iconColor = theme.palette.newColors?.gray || theme.palette.text.primary;
  const apiRef = useGridApiRef();
  const [rows, setRows] = useState([]);
  const [rowModesModel, setRowModesModel] = useState({});
  const [fclassDialog, setFclassDialog] = useState({ open: false, row: null });

  useEffect(() => {
    setRows(
      filterVisibleParameters(parameters).map((p) => ({
        id: p.paramKey,
        paramKey: p.paramKey,
        paramValue: p.paramValue ?? '',
        valueType: p.valueType,
        description: p.description ?? '',
        updatedAt: p.updatedAt,
        isVisible: true,
      })),
    );
  }, [parameters]);

  const handleRowEditStop = (params, event) => {
    if (params.reason === GridRowEditStopReasons.rowFocusOut) {
      event.defaultMuiPrevented = true;
    }
  };

  const processRowUpdate = async (newRow) => {
    const isFclass = FCLASS_KEYS.has(newRow.paramKey);
    let paramValue = newRow.paramValue;
    if (typeof paramValue === 'string' && paramValue.trim() === '') {
      paramValue = null;
    }
    if (isFclass && paramValue != null && typeof paramValue !== 'string') {
      paramValue = JSON.stringify(paramValue);
    }

    await modifyParameter({
      paramKey: newRow.paramKey,
      paramValue,
      description: newRow.description === '' ? null : newRow.description,
    });

    return {
      ...newRow,
      paramValue: paramValue ?? '',
    };
  };

  const handleEditClick = (id) => () => {
    setRowModesModel({ ...rowModesModel, [id]: { mode: GridRowModes.Edit } });
  };

  const handleSaveClick = (id) => () => {
    setRowModesModel({ ...rowModesModel, [id]: { mode: GridRowModes.View } });
  };

  const handleCancelClick = (id) => () => {
    setRowModesModel({
      ...rowModesModel,
      [id]: { mode: GridRowModes.View, ignoreModifications: true },
    });
    const original = (parameters || []).find((p) => p.paramKey === id);
    if (original) {
      setRows((prev) =>
        prev.map((r) =>
          r.id === id
            ? {
                ...r,
                paramValue: original.paramValue ?? '',
                description: original.description ?? '',
              }
            : r,
        ),
      );
    }
  };

  const openFclassDialog = useCallback((row) => {
    setFclassDialog({ open: true, row });
  }, []);

  const handleFclassConfirm = async (list) => {
    const row = fclassDialog.row;
    if (!row) return;
    const paramValue = list == null ? null : JSON.stringify(list);
    await modifyParameter({
      paramKey: row.paramKey,
      paramValue,
      description: row.description === '' ? null : row.description,
    });
    setFclassDialog({ open: false, row: null });
  };

  const columns = useMemo(
    () => [
      {
        field: 'paramKey',
        headerName: _(msg`Key`),
        flex: 1.2,
        minWidth: 200,
        editable: false,
        headerClassName: 'overload-theme--header',
      },
      {
        field: 'valueType',
        headerName: _(msg`Type`),
        width: 100,
        editable: false,
        headerClassName: 'overload-theme--header',
      },
      {
        field: 'paramValue',
        headerName: _(msg`Value`),
        flex: 1.4,
        minWidth: 220,
        editable: true,
        headerClassName: 'overload-theme--header',
        renderCell: (params) => {
          if (FCLASS_KEYS.has(params.row.paramKey)) {
            return (
              <Stack direction="row" spacing={1} alignItems="center" sx={{ width: '100%' }}>
                <Typography variant="body2" noWrap sx={{ flex: 1 }}>
                  {formatFclassSummary(params.row.paramValue, _)}
                </Typography>
                <Button
                  size="small"
                  startIcon={<ChecklistIcon />}
                  onClick={(e) => {
                    e.stopPropagation();
                    openFclassDialog(params.row);
                  }}
                >
                  <Trans>Edit</Trans>
                </Button>
              </Stack>
            );
          }
          const empty = params.value == null || params.value === '';
          return empty ? (
            <Typography variant="body2" color="text.secondary">
              <Trans>null</Trans>
            </Typography>
          ) : (
            String(params.value)
          );
        },
      },
      {
        field: 'description',
        headerName: _(msg`Description`),
        flex: 1.6,
        minWidth: 240,
        editable: true,
        headerClassName: 'overload-theme--header',
      },
      {
        field: 'updatedAt',
        headerName: _(msg`Updated`),
        width: 180,
        editable: false,
        headerClassName: 'overload-theme--header',
        valueFormatter: (value) => {
          if (!value) return '';
          try {
            return new Date(value).toLocaleString();
          } catch {
            return String(value);
          }
        },
      },
      {
        field: 'actions',
        type: 'actions',
        headerName: _(msg`Actions`),
        width: 100,
        headerClassName: 'overload-theme--header',
        getActions: ({ id, row }) => {
          if (FCLASS_KEYS.has(row.paramKey)) {
            return [
              <GridActionsCellItem
                key="fclass"
                icon={
                  <Tooltip title={_(msg`Edit road types`)}>
                    <ChecklistIcon sx={{ color: iconColor }} />
                  </Tooltip>
                }
                label={_(msg`Edit road types`)}
                onClick={() => openFclassDialog(row)}
                color="inherit"
              />,
            ];
          }
          const isInEditMode = rowModesModel[id]?.mode === GridRowModes.Edit;
          if (isInEditMode) {
            return [
              <GridActionsCellItem
                key="save"
                icon={<SvgCustomIcon name="save" color={iconColor} />}
                label="Save"
                onClick={handleSaveClick(id)}
                sx={{ color: 'primary.main' }}
              />,
              <GridActionsCellItem
                key="cancel"
                icon={<SvgCustomIcon name="cancel" color={iconColor} />}
                label="Cancel"
                onClick={handleCancelClick(id)}
                color="inherit"
              />,
            ];
          }
          return [
            <GridActionsCellItem
              key="edit"
              icon={<SvgCustomIcon name="edit" color={iconColor} />}
              label="Edit"
              onClick={handleEditClick(id)}
              color="inherit"
            />,
          ];
        },
      },
    ],
    [_, rowModesModel, openFclassDialog, iconColor],
  );

  const dialogTitle =
    fclassDialog.row?.paramKey === 'included_fclasses'
      ? _(msg`Included road types (whitelist)`)
      : _(msg`Excluded road types (blacklist)`);

  return (
    <Box>
      <Typography variant="body2" color="text.secondary" sx={{ mb: 1 }}>
        <Trans>
          Empty numeric/json values are saved as null (filter inactive). Road type lists use the Edit
          dialog.
        </Trans>
      </Typography>
      <Paper sx={{ width: '100%' }}>
        <StripedDataGrid
          apiRef={apiRef}
          rows={rows}
          columns={columns}
          editMode="row"
          rowModesModel={rowModesModel}
          onRowModesModelChange={setRowModesModel}
          onRowEditStop={handleRowEditStop}
          processRowUpdate={processRowUpdate}
          onProcessRowUpdateError={() => {}}
          isCellEditable={(params) =>
            !FCLASS_KEYS.has(params.row.paramKey) || params.field === 'description'
          }
          initialState={{ pagination: { paginationModel } }}
          pageSizeOptions={[25, 50, 100]}
          disableRowSelectionOnClick
          autoHeight
          getRowHeight={() => 'auto'}
          sx={{
            [`& .MuiDataGrid-cell`]: { py: 1, alignItems: 'center' },
          }}
        />
      </Paper>
      <RoadFclassPickerDialog
        open={fclassDialog.open}
        title={dialogTitle}
        selectedValues={parseJsonArray(fclassDialog.row?.paramValue)}
        onClose={() => setFclassDialog({ open: false, row: null })}
        onConfirm={handleFclassConfirm}
      />
    </Box>
  );
}
